// @ts-check

/**
 * Retrieves the selected option element from a select element.
 *
 * @param {HTMLSelectElement} selectElement - The select element to get the selected option from.
 * @returns {HTMLOptionElement} - The selected option element.
 */
const getSelectedOptionElementOfSelect = (selectElement) => {
  return selectElement.options[selectElement.selectedIndex]
}

/**
 * Custom element for managing the Christmas tree stand options on a product page.
 *
 * @class
 * @extends HTMLElement
 */
class ProductChristmasTreeStandOptionsCustom extends HTMLElement {
  /**
   * Constructs the custom element and initializes properties.
   * @constructor
   */
  constructor() {
    super()

    /** @type {HTMLSelectElement} */
    this.selectElement = /** @type {HTMLSelectElement} */ (this.querySelector('select'))

    /** @type {HTMLElement | null} */
    this.variantSelectsCustom = null

    /** @type {ProductFormCustom | null} */
    this.productFormCustom = null

    /** @type {HTMLFormElement} */
    this.form = /** @type {HTMLFormElement} */ (this.querySelector('form'))

    /** @type {number} */
    this.variantPrice = 0

    /** Initialize Slim Select instance */
    this.slimSelect = null
  }

  /**
   * Called when the element is connected to the DOM.
   * Initializes static elements and event listeners.
   */
  connectedCallback() {
    this.setStaticElements()
    this.setSelectListeners()
    this.initializeSlimSelect()
  }

  /**
   * Sets references to static elements used within the component.
   */
  setStaticElements() {
    /** @type {HTMLElement | null} */
    this.variantSelectsCustom = this.closest('variant-selects-custom')
    if (this.variantSelectsCustom && 'productFormCustom' in this.variantSelectsCustom) {
      /** @type {ProductFormCustom} */
      this.productFormCustom = /** @type {ProductFormCustom} */ (this.variantSelectsCustom.productFormCustom)
    }
  }

  /**
   * Initializes Slim Select on the select element.
   */
  initializeSlimSelect() {
    // Ensure Slim Select is loaded
    if (typeof SlimSelect !== 'undefined') {
      this.slimSelect = new SlimSelect({
        select: this.selectElement,
        settings: {
          showSearch: false,
        },
      })
    }
  }

  /**
   * Retrieves the price of the currently selected variant.
   *
   * @returns {number} - The price of the selected variant.
   */
  getVariantPrice() {
    const priceString = this.selectElement.options[this.selectElement.selectedIndex].dataset.price
    return parseFloat(priceString) || 0
  }

  /**
   * Sets the variant price attribute on the form element.
   */
  setVariantPrice() {
    this.variantPrice = this.getVariantPrice()
    this.form.setAttribute('data-variant-price', this.variantPrice.toString())
  }

  /**
   * Handles the change event for the select element.
   *
   * @param {Event} event - The change event object.
   */
  handleSelectChange(event) {
    if (this.productFormCustom) {
      this.productFormCustom.updateSubTotal()
    }
    this.setVariantPrice()
  }

  /**
   * Sets up event listeners for the select element.
   */
  setSelectListeners() {
    this.selectElement.addEventListener('change', this.handleSelectChange.bind(this))
  }
}

customElements.define('product-christmas-tree-stand-options-custom', ProductChristmasTreeStandOptionsCustom)
